import pickBy from 'lodash/pickBy';
import map from 'lodash/map';
import { PretaxAccountV2Type } from 'constants/accounts';

const toQueryString = (obj: Record<string, unknown>) => {
  const truthy_params = pickBy({
    ...obj,
  });
  const params = map(truthy_params, (v, k) => `${k}=${v}`);
  return `${params.length ? '?' : ''}${params.join('&')}`;
};

export const RootRoutes = Object.freeze({
  home: () => `/`,
  login: ({ type, message }: { type?: string; message?: string } = {}) =>
    `/login${toQueryString({ type, message })}`,
  loginPasswordReset: () => `/login/password-reset`,
});

export const ClaimRoutes = Object.freeze({
  new: ({ account_id }: { account_id?: string } = {}) =>
    `/claims/new${toQueryString({ account_id })}`,
  list: () => `/claims`,
  detailEmployerSponsored: ({ claim_id }: { claim_id: string }) =>
    `/claims/employer-sponsored/${claim_id}`,
  detailPretaxClaim: ({ claim_id }: { claim_id: string }) =>
    `/claims/pretax-claim/${claim_id}`,
  detailPretaxTransaction: ({
    transaction_id,
    settlement_date,
    seq_number,
  }: $TSFixMe) =>
    `/claims/pretax-transaction/${transaction_id}${toQueryString({
      settlement_date,
      seq_number,
    })}`,
  success: () => `/claims/success`,
});

export const AccountRoutes = Object.freeze({
  list: () => `/accounts`,
  inactive: () => `/accounts?status=inactive`,
  info: () => `/accounts/more-info`,
  detailEmployerSponsored: ({
    employee_wallet_id,
  }: {
    employee_wallet_id: string;
  }) => `/accounts/employer-sponsored/${employee_wallet_id}`,
  detailPretax: ({
    flex_account_id,
    page,
    type,
    date,
    expenses_page,
  }: {
    flex_account_id: string;
    page?: string;
    type?: string;
    date?: string;
    expenses_page?: string;
  }) =>
    `/accounts/pretax/${flex_account_id}${toQueryString({
      page,
      type,
      date,
      expenses_page,
    })}`,
});

export const ExternalStoreRoutes = Object.freeze({
  detail: ({
    account_type,
    store_name,
  }: {
    account_type: string;
    store_name: string;
  }) => `/external_store/${account_type}/${store_name}`,
});

export const StoreRoutes = Object.freeze({
  categoryLanding: ({
    alias,
    categoryId,
    walletId,
  }: {
    alias?: string;
    categoryId: string;
    walletId: string;
  }) =>
    `/explore/accounts/${walletId}/products/landing/${categoryId}${toQueryString(
      { alias }
    )}`,
  collection: ({ collection }: { collection: string }) =>
    `/explore/collection${toQueryString({ collection })}`,
  collectionNoCost: () => '/explore/collection?collection=no_cost',
  detailPretax: ({
    pretax_account,
    year,
  }: {
    pretax_account: string;
    year?: string | number;
  }) =>
    `/explore/pretax_accounts/${pretax_account}${toQueryString({
      year,
    })}`,
  landing2023NY: () => '/explore/landing2023ny',
  listExplore: () => `/explore`,
  listFeatured: ({
    account,
    employee_wallet_id,
  }: {
    account: string;
    employee_wallet_id: string;
  }) =>
    `/explore/accounts/${employee_wallet_id}/featured/${encodeURIComponent(
      account
    )}`,
  listCategory: ({
    alias,
    brands,
    category_id,
    employee_wallet_id,
    subcategories,
  }: {
    alias?: string;
    brands?: Array<string> | string;
    category_id: string;
    employee_wallet_id: string;
    subcategories?: Array<string> | string;
  }) =>
    `/explore/accounts/${employee_wallet_id}/products/${category_id}${toQueryString(
      { alias, brands, subcategories }
    )}`,
  search: ({ query }: { query: string }) =>
    `/search${toQueryString({ q: query })}`,
});

export const SettingsRoutes = Object.freeze({
  root: () => `/settings`,
  integrations: () => `/settings/integrations`,
  notifications: () => `/settings/notifications`,
  payment: () => `/settings/payment`,
  bankAccounts: () => `/settings/bank-accounts`,
  profile: () => `/settings/profile`,
  dependents: () => `/settings/dependents`,
  security: () => `/settings/security`,
  pretaxCardList: () => `/settings/pretax/cards`,
  pretaxCardNew: ({ dependent_id }: { dependent_id?: string } = {}) =>
    `/settings/pretax/cards/new${toQueryString({ dependent_id })}`,
  pretaxDependentsDetail: ({ dependent_id }: { dependent_id: string }) =>
    `/settings/pretax/dependents/${dependent_id}`,
  posttaxCardList: () => `/settings/employer-sponsored/cards`,
  posttaxCardNew: ({ card_type }: { card_type?: string } = {}) =>
    `/settings/employer-sponsored/cards/new${toQueryString({ card_type })}`,
  posttaxCardEdit: () => `/settings/employer-sponsored/cards/settings`,
  posttaxCardSuccess: ({
    stripe_card_id,
    card_type,
  }: {
    stripe_card_id: string;
    card_type?: string;
  }) =>
    `/settings/employer-sponsored/cards/${stripe_card_id}/success${toQueryString(
      { card_type }
    )}`,
  posttaxCardReplace: ({ stripe_card_id }: { stripe_card_id: string }) =>
    `/settings/employer-sponsored/cards/${stripe_card_id}/replace`,
  formaCard: () => `/settings/forma-card`,
});

export const WelcomeRoutes = Object.freeze({
  homeFeed: () => `/home-feed`,
  employerSponsored: () => `/welcome/employer-sponsored`,
  waysToSpend: () => `/welcome/ways-to-spend`,
  finishAccount: () => `/welcome/finish-account`,
  setUpPassword: () => `/welcome/set-up-password`,
  memberOnboarding: () => `/welcome/member-onboarding`,
  memberOnboardingReview: () => `/welcome/member-onboarding/review`,
  memberOnboardingActivate: () => `/welcome/member-onboarding/activate`,
});

export const VendorRoutes = Object.freeze({
  search: ({
    lat,
    long,
    name,
    wallet_id,
    activities,
    bounds,
    category_title,
  }: {
    lat?: string;
    long?: string;
    name?: string;
    wallet_id?: string;
    activities?: string;
    bounds?: string;
    category_title?: string;
  }) =>
    `/explore/search${toQueryString({
      lat,
      long,
      name,
      wallet_id,
      activities,
      bounds,
      category_title,
    })}`,
  gymAndFitness: ({
    lat,
    long,
    name,
    wallet_id,
    activities,
    bounds,
  }: {
    lat?: string;
    long?: string;
    name?: string;
    wallet_id?: string;
    activities?: string;
    bounds?: string;
  }) =>
    `/explore/gym-and-fitness${toQueryString({
      lat,
      long,
      name,
      wallet_id,
      activities,
      bounds,
    })}`,
  detail: ({
    vendor_id,
    product_id,
    lat,
    long,
    name,
    ref,
    vendorVenueId,
  }: {
    vendor_id?: string;
    product_id?: string;
    lat?: string;
    long?: string;
    name?: string;
    ref?: string;
    vendorVenueId?: string;
  }) =>
    `/explore/${vendor_id}/${product_id}${toQueryString({
      lat,
      long,
      name,
      ref,
      vendorVenueId,
    })}`,
});

export const SlackRoutes = Object.freeze({
  auth: () => `/slack/auth`,
  install: () => `/slack/install`,
});

export const TransactionRoutes = {
  detail: ({ transaction_id }: { transaction_id: string }) =>
    `/orders/${transaction_id}`,
  detailPosttaxCard: ({ transaction_id }: { transaction_id: string }) =>
    `/orders/card-transactions/${transaction_id}`,
  detailPretaxCard: ({ reference_id }: { reference_id: string }) =>
    `/pretax/card-transactions/${reference_id}`,
} as const;

export const SubscriptionRoutes = Object.freeze({
  detail: ({ subscription_id }: { subscription_id: string }) =>
    `/subscriptions/${subscription_id}/manage-membership`,
  manageMembership: ({ subscription_id }: { subscription_id: string }) =>
    `/subscriptions/${subscription_id}/manage-membership`,
  paymentsHistory: ({ subscription_id }: { subscription_id: string }) =>
    `/subscriptions/${subscription_id}/payments-history`,
});

export const HistoryRoutes = Object.freeze({
  subscriptionList: () => `/history/subscriptions`,
  orderList: () => `/history/orders`,
});

export const EnrollRoutes = Object.freeze({
  commuterContribution: ({
    plan,
    change_current = false,
    personal_default = 0,
  }: {
    plan: PretaxAccountV2Type;
    change_current?: boolean;
    personal_default?: number;
  }) =>
    `/accounts/pretax/commuter-enroll${toQueryString({
      plan,
      change_current,
      personal_default,
    })}`,
  commuterReview: ({
    plan_id,
    personal = 0,
    matched = 0,
    waived = false,
  }: {
    plan_id: string;
    personal?: number;
    matched?: number;
    waived?: boolean;
  }) =>
    `/accounts/pretax/commuter-enroll-review${toQueryString({
      plan_id,
      personal,
      matched,
      waived,
    })}`,
  commuterConfirmed: () => '/accounts/pretax/commuter-enroll-confirmed',
});

export const DependentRoutes = Object.freeze({
  new: () => `/dependents/new`,
});

export const HsaRoutes = Object.freeze({
  accountEnroll: ({ account_id }: { account_id: string }) =>
    `/accounts/${account_id}/enroll-hsa`,
  accountDetail: ({ flex_account_id }: { flex_account_id: string }) =>
    `/accounts/pretax/${flex_account_id}`,
  investmentDetail: ({
    accountId,
    ...rest
  }: {
    accountId: string;
    [key: string]: string;
  }) => `/accounts/hsa-investment/${accountId}${toQueryString(rest)}`,
  investmentSellOrders: ({ member_id }: { member_id: string }) =>
    `/accounts/hsa-investment/sell-orders/${member_id}`,
  investmentSellOrdersConfirmation: ({
    amount,
    member_id,
    sell_order,
  }: {
    amount: string;
    member_id: string;
    sell_order: string;
  }) =>
    `/accounts/hsa-investment/sell-orders-confirmation/${member_id}/${sell_order}/${amount}`,
  investMoney: ({ account_id }: { account_id: string }) =>
    `/accounts/hsa-investment/${account_id}/invest-money`,
  investmentAccountStatements: ({ account_id }: { account_id: string }) =>
    `/accounts/hsa-investment/${account_id}/documents`,
  investmentTaxDocuments: ({ account_id }: { account_id: string }) =>
    `/accounts/hsa-investment/${account_id}/documents?type=tax`,
  investmentTradeConfirmations: ({ account_id }: { account_id: string }) =>
    `/accounts/hsa-investment/${account_id}/documents?type=trade`,
  investSuccess: ({ account_id }: { account_id: string }) =>
    `/accounts/${account_id}/invest-success`,
  investDeferral: ({ account_id }: { account_id: string }) =>
    `/accounts/${account_id}/invest-later`,
  enrollSuccess: ({ account_id }: { account_id: string }) =>
    `/accounts/${account_id}/enroll-hsa-success`,
  reimbursementNew: ({ flex_account_id }: { flex_account_id: string }) =>
    `/accounts/pretax/${flex_account_id}/HSA/reimbursement/new`,
  reimbursementConfirm: ({ flex_account_id }: { flex_account_id: string }) =>
    `/accounts/pretax/${flex_account_id}/HSA/reimbursement/confirm`,
  expenseNew: ({ flex_account_id }: { flex_account_id: string }) =>
    `/accounts/pretax/${flex_account_id}/HSA/expense/new`,
  expenseConfirm: ({ flex_account_id }: { flex_account_id: string }) =>
    `/accounts/pretax/${flex_account_id}/HSA/expense/confirm`,
  alegeusBulkTransfer: () => `/accounts/hsa-transfer`,
  declineAlegeusBulkTransfer: () => `/accounts/hsa-transfer/decline`,
  accountStatements: ({ account_id }: { account_id: string }) =>
    `/accounts/hsa-cash/${account_id}/documents`,
});

export const AlegeusRoutes = {
  alegeusHsa: ({ accountId }: { accountId: string }) =>
    `/saml/alegeus?accountId=${accountId}&page=AccountsDetailsHsa&options=0`,
  alegeusAccount: () => `/saml/alegeus`,
} as const;

export const SupportRoutes = Object.freeze({
  helpCenter: () => 'https://support.joinforma.com/en/',
});
